import { get, ref, set } from "firebase/database";
import { db } from "../firebaseConfig.js";

export const USER_BACKUP_VERSION = 2;
export const USER_BACKUP_KEYS = [
  "historygps",
  "meusTrilhos",
  "trilhos",
  "plannedTrailFiles",
  "trekkin_recording",
  "trekkin_recording_draft_v1",
  "trekkin_recording_journal_v1",
  "trekkin.activityMode",
  "trekkin_health_weight_kg",
  "trekkin.heartRate.source",
  "trekkin_language",
  "trekkin.theme",
  "offTrailAlert",
  "offTrailThresholdM",
  "autoAlertsEnabled",
  "autoAlertFauna",
  "autoAlertTrilho",
  "autoAlertSeguranca",
  "autoAlertCriticos",
  "autoAlertVibrate",
  "autoAlertSound",
  "chatAlertsEnabled",
  "alertFeedbackMode",
  "waypointsCategoryPalette",
  "waypointsSelectedCategories",
  "waypointsLayerOn",
];

const MEANINGFUL_KEYS = new Set([
  "historygps",
  "meusTrilhos",
  "trilhos",
  "plannedTrailFiles",
  "trekkin_recording",
  "trekkin_recording_draft_v1",
  "trekkin_recording_journal_v1",
]);

export function collectLocalBackup(storage = globalThis.localStorage) {
  const values = {};
  USER_BACKUP_KEYS.forEach((key) => {
    const value = storage?.getItem?.(key);
    if (value !== null && value !== undefined && String(value).length > 0) {
      values[key] = String(value);
    }
  });
  return values;
}

export function hasMeaningfulLocalBackupData(storage = globalThis.localStorage) {
  return USER_BACKUP_KEYS.some((key) => {
    if (!MEANINGFUL_KEYS.has(key)) return false;
    const raw = String(storage?.getItem?.(key) || "").trim();
    if (!raw || raw === "[]" || raw === "{}" || raw === "null") return false;
    return true;
  });
}

export function restoreLocalBackupValues(values, storage = globalThis.localStorage) {
  if (!values || typeof values !== "object") {
    throw new Error("invalid_backup");
  }
  const normalizedValues = Array.isArray(values)
    ? Object.fromEntries(
        values
          .filter((entry) => entry && typeof entry.key === "string" && typeof entry.value === "string")
          .map((entry) => [entry.key, entry.value])
      )
    : values;
  let restored = 0;
  USER_BACKUP_KEYS.forEach((key) => {
    if (typeof normalizedValues[key] !== "string") return;
    storage?.setItem?.(key, normalizedValues[key]);
    restored += 1;
  });
  return restored;
}

export async function saveUserBackup(uid, { appVersion = "" } = {}) {
  const userId = String(uid || "").trim();
  if (!userId) throw new Error("missing_user");
  const collectedValues = collectLocalBackup();
  // Realtime Database removes empty objects. Keep a harmless marker so a
  // first backup (before the user has created a route) still passes the
  // server validation and can later be restored normally.
  // RTDB keys cannot contain dots, while several localStorage keys do. Store
  // entries as an array so names such as `trekkin.activityMode` remain intact.
  const values = Object.keys(collectedValues).length
    ? Object.entries(collectedValues).map(([key, value]) => ({ key, value }))
    : [{ key: "__trekkinEmptyBackup", value: "1" }];
  const payload = {
    version: USER_BACKUP_VERSION,
    ownerUid: userId,
    createdAt: Date.now(),
    appVersion: String(appVersion || ""),
    values,
  };
  await set(ref(db, `userBackups/${userId}/latest`), payload);
  return payload;
}

export async function loadUserBackup(uid) {
  const userId = String(uid || "").trim();
  if (!userId) return null;
  const snapshot = await get(ref(db, `userBackups/${userId}/latest`));
  const value = snapshot.val();
  if (!value || value.ownerUid !== userId || !value.values) return null;
  return value;
}

export async function restoreUserBackup(uid) {
  const backup = await loadUserBackup(uid);
  if (!backup) throw new Error("backup_not_found");
  const restored = restoreLocalBackupValues(backup.values);
  return { backup, restored };
}
